import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { api } from './client';
import type {
  AvailabilityResponse,
  BookingMode,
  BookingPayload,
  BookingResponse,
  ConfirmVerificationResult,
  MarketplaceCategory,
  MarketplaceCity,
  MarketplaceCountry,
  MarketplaceEmployee,
  MarketplaceUser,
  MyBooking,
  SalonProfile,
  SearchParams,
  SearchResponse,
  StartVerificationResult,
} from './contracts';

// Single source of truth for types lives in ./contracts; the redesign hooks
// live in sibling files. Re-exported so `from '../api/use-marketplace'` keeps
// working everywhere.
export * from './contracts';
export * from './errors';
export { api, apiOrNull, marketplaceKeys, API_BASE_URL } from './client';
export * from './use-booking-flow';
export * from './use-my-account';
export * from './use-reviews';
export * from './use-alerts';

// ─── Queries ───────────────────────────────────────────────────────────

/**
 * Distinct cities we have marketplace-listed salons in, ordered by
 * salon-count descending. Used to power the city autocomplete in the
 * search bar — it's a suggestion list, not a hard filter.
 */
export function useCities(country?: string | null) {
  const qs = new URLSearchParams();
  if (country) qs.set('country', country);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery<MarketplaceCity[]>({
    queryKey: ['marketplace-cities', country ?? null],
    queryFn: () => api<MarketplaceCity[]>(`/api/v1/marketplace/cities${suffix}`),
    staleTime: 5 * 60_000,
  });
}

export function useCategories() {
  return useQuery<MarketplaceCategory[]>({
    queryKey: ['marketplace-categories'],
    queryFn: () => api<MarketplaceCategory[]>(`/api/v1/marketplace/categories?marketplace=beauty`),
    staleTime: 5 * 60_000,
  });
}

/**
 * Countries we have marketplace-listed salons in, with salon counts. Powers
 * the country-domain landing pages. Country-independent (no filter param).
 */
export function useCountries() {
  return useQuery<MarketplaceCountry[]>({
    queryKey: ['marketplace-countries'],
    queryFn: () => api<MarketplaceCountry[]>(`/api/v1/marketplace/countries`),
    staleTime: 5 * 60_000,
  });
}

/** Serializes search params to the `/search` query string (stable order → stable cache key). */
export function buildSearchQuery(params: SearchParams): string {
  const qs = new URLSearchParams();
  qs.set('marketplace', 'beauty');
  if (params.category) qs.set('category', params.category);
  if (params.subcategory) qs.set('subcategory', params.subcategory);
  if (params.q) qs.set('q', params.q);
  if (params.city) qs.set('city', params.city);
  if (params.country) qs.set('country', params.country);
  if (params.lat != null) qs.set('lat', String(params.lat));
  if (params.lng != null) qs.set('lng', String(params.lng));
  if (params.radius != null) qs.set('radius', String(params.radius));
  if (params.page != null) qs.set('page', String(params.page));
  if (params.onlineAvailable) qs.set('onlineAvailable', 'true');
  if (params.mobileAvailable) qs.set('mobileAvailable', 'true');
  if (params.audience) qs.set('audience', params.audience);
  // Redesign filters (§1). Server defaults are omitted to keep keys/URLs short.
  if (params.when && params.when !== 'any') qs.set('when', params.when);
  if (params.sort && params.sort !== 'recommended') qs.set('sort', params.sort);
  if (params.minRating != null) qs.set('minRating', String(params.minRating));
  if (params.priceMax != null) qs.set('priceMax', String(params.priceMax));
  if (params.payOnline) qs.set('payOnline', 'true');
  if (params.accessible) qs.set('accessible', 'true');
  if (params.nextSlots != null) qs.set('nextSlots', String(params.nextSlots));
  qs.set('size', String(params.size ?? 24));
  return qs.toString();
}

export function useSearch(params: SearchParams, options: { enabled?: boolean } = {}) {
  const qs = buildSearchQuery(params);
  return useQuery<SearchResponse>({
    queryKey: ['marketplace-search', qs],
    queryFn: () => api<SearchResponse>(`/api/v1/marketplace/search?${qs}`),
    staleTime: 60_000,
    enabled: options.enabled ?? true,
  });
}

export function useSalon(subdomain: string | undefined) {
  return useQuery<SalonProfile>({
    queryKey: ['marketplace-salon', subdomain],
    queryFn: () => api<SalonProfile>(`/api/v1/marketplace/salon/${subdomain}`),
    enabled: !!subdomain,
    staleTime: 60_000,
  });
}

/** Returns the signed-in user or null on 401 (resolved, not thrown). */
export function useMe() {
  return useQuery<MarketplaceUser | null>({
    queryKey: ['marketplace-me'],
    queryFn: async () => {
      try {
        const r = await api<{ marketplaceUser: MarketplaceUser }>(`/api/v1/marketplace/auth/me`);
        return r.marketplaceUser;
      } catch (e) {
        const err = e as { status?: number };
        if (err.status === 401) return null;
        throw e;
      }
    },
    staleTime: 30_000,
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { fullName: string | null; phone: string | null }) =>
      api<{ marketplaceUser: MarketplaceUser }>(`/api/v1/marketplace/auth/me`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: (r) => {
      // Reflect the saved profile immediately and refetch on next read.
      qc.setQueryData(['marketplace-me'], r.marketplaceUser);
      qc.invalidateQueries({ queryKey: ['marketplace-me'] });
    },
  });
}

export function useMyBookings(past = false) {
  return useQuery<MyBooking[]>({
    queryKey: ['marketplace-my-bookings', past],
    queryFn: async () => {
      const r = await api<{ bookings: MyBooking[] }>(
        `/api/v1/marketplace/me/bookings?past=${past}`,
      );
      return r.bookings;
    },
    staleTime: 30_000,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────

/**
 * Sends `language` (the UI's current i18n language, the same code `/book` and
 * `/booking-verifications/start` send) so the backend localizes the sign-in email.
 */
export function useRequestMagicLink() {
  const { i18n } = useTranslation();
  return useMutation({
    mutationFn: (email: string) =>
      api<void>(`/api/v1/marketplace/auth/request-magic-link`, {
        method: 'POST',
        body: JSON.stringify({ email, language: i18n.language || 'en' }),
      }),
  });
}

export function useConsumeMagicLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      api<{ marketplaceUser: MarketplaceUser }>(
        `/api/v1/marketplace/auth/consume-magic-link`,
        {
          method: 'POST',
          body: JSON.stringify({ token }),
        },
      ),
    onSuccess: (data) => {
      qc.setQueryData(['marketplace-me'], data.marketplaceUser);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<void>(`/api/v1/marketplace/auth/logout`, { method: 'POST' }),
    onSuccess: () => {
      qc.setQueryData(['marketplace-me'], null);
      qc.invalidateQueries({ queryKey: ['marketplace-my-bookings'] });
    },
  });
}

/**
 * Active employees at a marketplace salon, optionally filtered to those
 * qualified for a service AND who work at the given location.
 */
export function useEmployees(
  subdomain: string,
  params: {
    serviceId: string | null;
    locationId: string | null;
    /**
     * Pass MOBILE / ONLINE to list staff without a location (backend M6). The
     * request then carries no locationId; an older backend that can't answer
     * just yields no staff chips.
     */
    bookingMode?: 'AT_LOCATION' | 'MOBILE' | 'ONLINE';
  },
) {
  const locationless = params.bookingMode === 'MOBILE' || params.bookingMode === 'ONLINE';
  const enabled = !!(subdomain && params.serviceId && (params.locationId || locationless));
  return useQuery<MarketplaceEmployee[]>({
    queryKey: ['marketplace-employees', subdomain, params.serviceId, locationless ? params.bookingMode : params.locationId],
    enabled,
    queryFn: () => {
      const qs = new URLSearchParams();
      qs.set('serviceId', params.serviceId as string);
      if (locationless) qs.set('bookingMode', params.bookingMode as string);
      else qs.set('locationId', params.locationId as string);
      return api<MarketplaceEmployee[]>(
        `/api/v1/marketplace/salon/${subdomain}/employees?${qs.toString()}`,
      );
    },
    staleTime: 60_000,
  });
}

/**
 * Returns the bookable slot list for a given salon + service + location +
 * date. `enabled` is wired via the optional `enabled` arg so the dialog
 * can defer the call until the user has picked a date.
 */
export function useAvailability(
  subdomain: string,
  params: {
    date: string | null;
    serviceId: string | null;
    locationId: string | null;
    employeeId?: string | null;
    /**
     * Optional. When set to MOBILE/ONLINE, BE applies mode-specific slot
     * filtering (e.g. ignores fixed-location bounds for MOBILE; treats the
     * service as remote for ONLINE).
     */
    bookingMode?: BookingMode;
  },
) {
  // MOBILE/ONLINE have no physical location — the BE only requires a
  // locationId for AT_LOCATION. Mirror that here so online/mobile-only salons
  // can still load slots (otherwise the query never runs and no times show).
  const locationless = params.bookingMode === 'MOBILE' || params.bookingMode === 'ONLINE';
  const enabled = !!(
    params.date &&
    params.serviceId &&
    subdomain &&
    (params.locationId || locationless)
  );
  return useQuery<AvailabilityResponse>({
    queryKey: [
      'marketplace-availability',
      subdomain,
      params.date,
      params.serviceId,
      params.locationId,
      params.employeeId ?? null,
      params.bookingMode ?? null,
    ],
    enabled,
    queryFn: () => {
      const qs = new URLSearchParams();
      qs.set('date', params.date as string);
      qs.set('serviceId', params.serviceId as string);
      // Only send locationId when we actually have one (AT_LOCATION). For
      // MOBILE/ONLINE it's absent — sending "null" would 400 on the BE.
      if (params.locationId) qs.set('locationId', params.locationId);
      if (params.employeeId) qs.set('employeeId', params.employeeId);
      if (params.bookingMode === 'MOBILE' || params.bookingMode === 'ONLINE') {
        qs.set('bookingMode', params.bookingMode);
      }
      return api<AvailabilityResponse>(
        `/api/v1/marketplace/salon/${subdomain}/availability?${qs.toString()}`,
      );
    },
    staleTime: 30_000,
  });
}

export function useBook(subdomain: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: BookingPayload) =>
      api<BookingResponse>(`/api/v1/marketplace/salon/${subdomain}/book`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketplace-my-bookings'] });
    },
  });
}

export function useStartBookingVerification() {
  return useMutation<StartVerificationResult, Error, { email: string; language?: string }>({
    mutationFn: (vars) =>
      api<StartVerificationResult>(`/api/v1/marketplace/booking-verifications/start`, {
        method: 'POST',
        body: JSON.stringify(vars),
      }),
  });
}

export function useConfirmBookingVerification() {
  return useMutation<ConfirmVerificationResult, Error, { email: string; code: string }>({
    mutationFn: (vars) =>
      api<ConfirmVerificationResult>(`/api/v1/marketplace/booking-verifications/confirm`, {
        method: 'POST',
        body: JSON.stringify(vars),
      }),
  });
}
